import Vue from 'vue';
import VueRouter, { isNavigationFailure, RawLocation, Route } from 'vue-router';
import Chatroom from '../views/Chatroom/index.vue';
import Article from '../views/article.vue';
import MyRequest from '../views/myRequest.vue'; // Only used for open directly
import NewHome from '../views/NewHome/NewHome.vue';
import NewHelp from '../views/NewHelp/NewHelp.vue';
import NewArticle from '../views/NewArticle/NewArticle.vue';
import AllConversations from '../views/Conversations/AllConversations.vue';
import ConversationDetails from '../views/Conversations/ConversationDetails.vue';
import NewCsat from '../views/NewCsat/NewCsat.vue';
import { isArticleOpen, isMyRequestOpen, emitOpenArticle, emitOpenMyRequest } from '@/common/eventBus';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: Chatroom,
  },
  {
    path: '/article/:id',
    name: 'article',
    component: Article,
  },
  {
    path: '/myrequest',
    name: 'myrequest',
    component: MyRequest,
  },
  {
    path: '/new-home',
    name: 'new-home',
    component: NewHome,
  },
  {
    path: '/new-help',
    name: 'new-help',
    component: NewHelp,
  },
  {
    path: '/new-article/:id',
    name: 'new-article',
    component: NewArticle,
  },
  {
    path: '/conversations',
    name: 'conversations',
    component: AllConversations,
  },
  {
    path: '/conversation/:id',
    name: 'conversation-details',
    component: ConversationDetails,
  },
  {
    path: '/new-csat',
    name: 'new-csat',
    component: NewCsat,
  },
];

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    // Only apply scroll behavior for new chatbot routes
    const newChatbotRouteNames = ['new-home', 'new-help', 'new-article', 'conversations', 'conversation-details'];

    const isNewChatbotRoute = to.name && newChatbotRouteNames.includes(to.name as string);

    // For old chatbot routes, don't apply any scroll behavior
    if (!isNewChatbotRoute) {
      return undefined;
    }

    // For new chatbot routes, restore saved position if available
    if (savedPosition) {
      return savedPosition;
    }

    // Same route record and path — only query or hash changed (e.g. conversations pagination).
    // Returning a falsy value skips scroll (see vue-router handleScroll).
    if (to.name === from.name && to.path === from.path) {
      return savedPosition;
    }

    // Scroll to top for new chatbot routes
    return { x: 0, y: 0 };
  },
});

/**
 * vue-router rejects redundant, aborted and cancelled navigations. Every call site here is
 * fire-and-forget (the widget re-enters the same route on re-init, and `beforeEach` aborts to
 * close an open article), so those rejections had no handler and surfaced as unhandled
 * `NavigationDuplicated` errors in error tracking.
 */
(['push', 'replace'] as const).forEach((method) => {
  const navigate = router[method].bind(router) as (location: RawLocation) => Promise<Route>;

  router[method] = ((location: RawLocation) =>
    navigate(location).catch((error: unknown) => {
      if (isNavigationFailure(error)) return router.currentRoute;
      throw error;
    })) as VueRouter[typeof method];
});

router.beforeEach((to, from, next) => {
  // When user click back on his phone, if Aritcle Component (src/components/Article.vue) is show. just close it and leave in the same page: next().
  // Same as MyRequest (src/components/MyRequest/index.vue)
  // Those two components are imported and maintained in App.vue
  if (isArticleOpen || isMyRequestOpen) {
    emitOpenArticle({
      isOpen: false,
    });
    emitOpenMyRequest(false);
    next(false);
    return;
  }

  next();
});

export default router;
